import { useEffect, useState } from 'react';

interface FloatingElement {
  id: number;
  x: number;
  y: number;
  type: 'bracket' | 'keyword' | 'symbol' | 'ai';
  content: string;
  opacity: number;
  scale: number;
  rotation: number;
  speed: number;
}

const keywords = ['function', 'const', 'async', 'await', 'return', 'import', 'export', 'class', 'interface', 'type'];
const brackets = ['{ }', '( )', '[ ]', '< >', '=> ', '...'];
const symbols = ['✨', '⚡', '🔧', '💡', '🎯', '🚀'];
const aiTexts = ['AI', 'GPT', 'LLM', 'ML', '🤖'];

export function IDEBackground() {
  const [elements, setElements] = useState<FloatingElement[]>([]);
  const [particles, setParticles] = useState<{ id: number; x: number; y: number; vx: number; vy: number }[]>([]);

  useEffect(() => {
    // Create floating elements
    const initialElements: FloatingElement[] = [];

    for (let i = 0; i < 20; i++) {
      const types: ('bracket' | 'keyword' | 'symbol' | 'ai')[] = ['bracket', 'keyword', 'symbol', 'ai'];
      const type = types[Math.floor(Math.random() * types.length)];

      let content = '';
      if (type === 'bracket') content = brackets[Math.floor(Math.random() * brackets.length)];
      else if (type === 'keyword') content = keywords[Math.floor(Math.random() * keywords.length)];
      else if (type === 'symbol') content = symbols[Math.floor(Math.random() * symbols.length)];
      else content = aiTexts[Math.floor(Math.random() * aiTexts.length)];

      initialElements.push({
        id: i,
        x: Math.random() * 100,
        y: Math.random() * 100,
        type,
        content,
        opacity: 0.03 + Math.random() * 0.08,
        scale: 0.8 + Math.random() * 0.4,
        rotation: Math.random() * 30 - 15,
        speed: 0.2 + Math.random() * 0.3,
      });
    }
    setElements(initialElements);

    // Create connecting particles
    const initialParticles = Array.from({ length: 15 }, (_, i) => ({
      id: i,
      x: Math.random() * 100,
      y: Math.random() * 100,
      vx: (Math.random() - 0.5) * 0.3,
      vy: (Math.random() - 0.5) * 0.3,
    }));
    setParticles(initialParticles);
  }, []);

  // Animate elements floating
  useEffect(() => {
    const interval = setInterval(() => {
      setElements(prev =>
        prev.map(el => ({
          ...el,
          y: el.y <= -5 ? 105 : el.y - el.speed * 0.15,
          x: el.x + Math.sin(Date.now() / 2000 + el.id) * 0.03,
          rotation: el.rotation + Math.sin(Date.now() / 3000 + el.id) * 0.1,
        }))
      );
    }, 50);
    return () => clearInterval(interval);
  }, []);

  // Animate particles
  useEffect(() => {
    const interval = setInterval(() => {
      setParticles(prev =>
        prev.map(p => {
          let newX = p.x + p.vx;
          let newY = p.y + p.vy;
          let newVx = p.vx;
          let newVy = p.vy;

          if (newX <= 0 || newX >= 100) newVx = -newVx;
          if (newY <= 0 || newY >= 100) newVy = -newVy;

          return { ...p, x: newX, y: newY, vx: newVx, vy: newVy };
        })
      );
    }, 50);
    return () => clearInterval(interval);
  }, []);

  // Find connections between nearby particles
  const connections: { x1: number; y1: number; x2: number; y2: number; opacity: number }[] = [];
  particles.forEach((p1, i) => {
    particles.slice(i + 1).forEach(p2 => {
      const dist = Math.sqrt(Math.pow(p1.x - p2.x, 2) + Math.pow(p1.y - p2.y, 2));
      if (dist < 25) {
        connections.push({
          x1: p1.x,
          y1: p1.y,
          x2: p2.x,
          y2: p2.y,
          opacity: (1 - dist / 25) * 0.15,
        });
      }
    });
  });

  return (
    <div className="absolute inset-0 overflow-hidden pointer-events-none">
      {/* Base gradient */}
      <div className="absolute inset-0 bg-gradient-to-b from-transparent via-bg-primary/50 to-transparent" />

      {/* Floating code elements */}
      {elements.map(el => (
        <div
          key={el.id}
          className={`absolute font-mono transition-all duration-1000 ${
            el.type === 'keyword' ? 'text-purple-400' :
            el.type === 'bracket' ? 'text-blue-400' :
            el.type === 'ai' ? 'text-accent font-bold' :
            ''
          }`}
          style={{
            left: `${el.x}%`,
            top: `${el.y}%`,
            opacity: el.opacity,
            transform: `translate(-50%, -50%) scale(${el.scale}) rotate(${el.rotation}deg)`,
            fontSize: el.type === 'symbol' ? '1.5rem' : '0.75rem',
          }}
        >
          {el.content}
        </div>
      ))}

      {/* SVG for particle connections */}
      <svg className="absolute inset-0 w-full h-full">
        {connections.map((conn, i) => (
          <line
            key={i}
            x1={`${conn.x1}%`}
            y1={`${conn.y1}%`}
            x2={`${conn.x2}%`}
            y2={`${conn.y2}%`}
            stroke="#6366f1"
            strokeWidth="1"
            opacity={conn.opacity}
          />
        ))}
        {particles.map(p => (
          <circle
            key={p.id}
            cx={`${p.x}%`}
            cy={`${p.y}%`}
            r="2"
            fill="#6366f1"
            opacity="0.3"
          />
        ))}
      </svg>

      {/* Subtle glow spots */}
      <div
        className="absolute top-1/4 left-1/4 w-64 h-64 rounded-full opacity-10"
        style={{
          background: 'radial-gradient(circle, rgba(99, 102, 241, 0.4) 0%, transparent 70%)',
        }}
      />
      <div
        className="absolute bottom-1/4 right-1/4 w-48 h-48 rounded-full opacity-10"
        style={{
          background: 'radial-gradient(circle, rgba(168, 85, 247, 0.4) 0%, transparent 70%)',
        }}
      />
    </div>
  );
}
